/**
 * Grabar una nota de voz desde el compositor del chat.
 *
 * El botón del micrófono era un `toast.info('Audio próximamente')` desde que
 * existe el compositor. Ahora graba de verdad: `MediaRecorder` con opus donde
 * se puede y mp4 en Safari (design.md · D6), tope de 5 minutos, y el resultado
 * entra a la bandeja de adjuntos como cualquier archivo.
 *
 * La transcripción NO se hace acá: la pide el envío, para que el cuerpo del
 * mensaje sea el texto dictado (design.md · D5).
 */

import { Check, Mic, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';

import { NUM_MONO_MICRO } from '@/features/chat/components/composer/numero';

/** Tope de grabación. Más que esto no es una nota de voz. */
export const MAX_GRABACION_MS = 5 * 60_000;

interface Props {
    disabled?: boolean;
    grabando: boolean;
    onGrabandoChange: (grabando: boolean) => void;
    onGrabada: (blob: Blob, nombre: string, duracionMs: number) => void;
}

/**
 * Formato que el navegador puede grabar. Chrome, Firefox y Android hacen opus
 * en webm; Safari solo mp4. Se elige el primero soportado y el MIME real viaja
 * con el blob para que el reproductor y el transcriptor sepan qué recibieron.
 */
export function elegirFormato(
    soportado: (t: string) => boolean = (t) =>
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t),
): { mimeType: string; extension: string } {
    const candidatos: Array<{ mimeType: string; extension: string }> = [
        { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
        { mimeType: 'audio/webm', extension: 'webm' },
        { mimeType: 'audio/mp4', extension: 'm4a' },
        { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg' },
    ];
    for (const c of candidatos) {
        try {
            if (soportado(c.mimeType)) return c;
        } catch {
            // isTypeSupported puede tirar en navegadores viejos: se sigue.
        }
    }
    // Sin ninguno soportado, se deja que el navegador elija su default.
    return { mimeType: '', extension: 'webm' };
}

/** mm:ss para el contador. */
export function formatearDuracion(ms: number): string {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

/** Por qué no se pudo grabar, en palabras que sirvan. */
export function motivoDeFalloDeMicrofono(e: unknown): string {
    const nombre = (e as { name?: string } | null)?.name ?? '';
    if (nombre === 'NotAllowedError' || nombre === 'SecurityError') {
        return 'El navegador bloqueó el micrófono. Habilitalo para este sitio y volvé a intentar.';
    }
    if (nombre === 'NotFoundError' || nombre === 'DevicesNotFoundError') {
        return 'No se encontró ningún micrófono conectado.';
    }
    if (nombre === 'NotReadableError') {
        return 'El micrófono está siendo usado por otra aplicación.';
    }
    return 'No se pudo acceder al micrófono.';
}

export function VoiceRecorder({ disabled, grabando, onGrabandoChange, onGrabada }: Props) {
    const [transcurrido, setTranscurrido] = useState(0);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const trozosRef = useRef<BlobPart[]>([]);
    const inicioRef = useRef(0);
    const cancelarRef = useRef(false);
    const intervaloRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const soltarTodo = useCallback(() => {
        if (intervaloRef.current) {
            clearInterval(intervaloRef.current);
            intervaloRef.current = null;
        }
        const rec = recorderRef.current;
        rec?.stream.getTracks().forEach((t) => t.stop());
        recorderRef.current = null;
        trozosRef.current = [];
        setTranscurrido(0);
    }, []);

    // Si el compositor se desmonta mientras graba, el micrófono tiene que
    // apagarse igual: si no, el navegador deja el indicador rojo prendido.
    useEffect(() => soltarTodo, [soltarTodo]);

    async function arrancar() {
        if (disabled || grabando) return;
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (e) {
            toast.error('No se pudo grabar', { description: motivoDeFalloDeMicrofono(e) });
            onGrabandoChange(false);
            return;
        }

        const { mimeType, extension } = elegirFormato();
        let rec: MediaRecorder;
        try {
            rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        } catch (e) {
            stream.getTracks().forEach((t) => t.stop());
            toast.error('No se pudo grabar', { description: motivoDeFalloDeMicrofono(e) });
            onGrabandoChange(false);
            return;
        }

        trozosRef.current = [];
        cancelarRef.current = false;
        inicioRef.current = Date.now();
        recorderRef.current = rec;

        rec.ondataavailable = (e) => {
            if (e.data.size > 0) trozosRef.current.push(e.data);
        };
        rec.onstop = () => {
            const duracion = Date.now() - inicioRef.current;
            const trozos = trozosRef.current;
            const cancelado = cancelarRef.current;
            const tipo = rec.mimeType || mimeType || 'audio/webm';
            soltarTodo();
            onGrabandoChange(false);
            if (cancelado || trozos.length === 0) return;
            const blob = new Blob(trozos, { type: tipo });
            onGrabada(blob, `nota-de-voz-${Date.now()}.${extension}`, duracion);
        };

        rec.start();
        onGrabandoChange(true);
        intervaloRef.current = setInterval(() => {
            const ms = Date.now() - inicioRef.current;
            setTranscurrido(ms);
            if (ms >= MAX_GRABACION_MS) {
                toast.info('Nota de voz cortada', {
                    description: 'El máximo es 5 minutos.',
                });
                recorderRef.current?.stop();
            }
        }, 200);
    }

    function terminar() {
        cancelarRef.current = false;
        recorderRef.current?.stop();
    }

    function cancelar() {
        cancelarRef.current = true;
        recorderRef.current?.stop();
    }

    if (!grabando) {
        return (
            <button
                type="button"
                title="Grabar una nota de voz"
                aria-label="Grabar una nota de voz"
                disabled={disabled}
                onClick={() => void arrancar()}
            >
                <Mic className="h-3.5 w-3.5" />
            </button>
        );
    }

    return (
        <span className="flex items-center gap-1.5">
            <span className={cn('text-fg-muted', NUM_MONO_MICRO)}>
                {formatearDuracion(transcurrido)}
            </span>
            <button
                type="button"
                title="Descartar la grabación"
                aria-label="Descartar la grabación"
                onClick={cancelar}
                className="hover:text-destructive"
            >
                <X className="h-3.5 w-3.5" />
            </button>
            <button
                type="button"
                title="Terminar la grabación"
                aria-label="Terminar la grabación"
                onClick={terminar}
                className={cn('text-brand')}
            >
                <Check className="h-3.5 w-3.5" />
            </button>
        </span>
    );
}
